/**
 * @fileoverview Tabbed detail slide-in for the System-web Violation Code Sets grid.
 * @module system-web/components/CodeSetDetailSlideout
 *
 * Responsibilities:
 * - Render a four-tab slide-in (General, Cache Status, Where Used, Audit) for a code set row
 * - Work for both cached artifact rows and in-flight background-job rows
 *
 * Dependencies:
 * - `@firesquirrel/local-db` — background job contracts and Dexie handles
 * - `../services/tenantService` — local-first tenant reads for "Where Used"
 *
 * Architecture:
 * - Reads only from Dexie. No API calls are issued here
 * - Cache-action callbacks (rebuild/archive/verify) are owned by the page; this component only renders buttons
 *
 * Notes:
 * - The Audit tab fuses local `audit_logs` rows with historical `background_jobs` rows
 *   for the same (vendorId, bookId) to approximate a cache lifecycle view.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, BookOpen, Database, ExternalLink, History, Layers3, Radio, RotateCcw, ShieldCheck, Users } from 'lucide-react';
import { getSystemDb, type BackgroundJob } from '@firesquirrel/local-db';
import { listTenants } from '../services/tenantService';
import type {
  SystemViolationCodeSetArtifact,
} from '../services/systemViolationCodeSetLibraryService';
import type { CodeSetCacheVerifyReport } from '../services/codeSetCacheActions';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDateTime(dateValue?: string | Date | null): string {
  if (!dateValue) return '—';
  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatBytes(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function statusBadgeClass(status: string): string {
  if (status === 'ready' || status === 'completed' || status === 'ok') return 'bg-green-100 text-green-700 border border-green-200';
  if (status === 'downloading' || status === 'rebuilding' || status === 'running') return 'bg-blue-100 text-blue-700 border border-blue-200';
  if (status === 'queued' || status === 'pending') return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
  if (status === 'archiving') return 'bg-amber-100 text-amber-700 border border-amber-200';
  if (status === 'failed' || status === 'corrupted' || status === 'error') return 'bg-red-100 text-red-700 border border-red-200';
  return 'bg-gray-100 text-gray-700 border border-gray-200';
}

function friendlyStatus(status: string): string {
  if (status === 'pending') return 'Queued';
  if (status === 'running') return 'Downloading';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function resolveLiveJob(props: CodeSetDetailSlideoutProps): BackgroundJob | null {
  if (props.job) return props.job;
  return props.backgroundJobs.find((candidate) => {
    const payload = (candidate.payload ?? {}) as Record<string, unknown>;
    return payload.vendorId === props.vendorId
      && payload.bookId === props.bookId
      && (candidate.status === 'running' || candidate.status === 'pending');
  }) ?? null;
}

function formatElapsed(startedAt?: string, nowMs?: number): string {
  if (!startedAt) return '—';
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return '—';
  const end = nowMs ?? Date.now();
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s.toString().padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm.toString().padStart(2, '0')}m`;
}

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract CodeSetDetailSlideoutProps
 *
 * Purpose:
 * - Inputs required to render the tabbed detail view for one code set row.
 *
 * Fields:
 * - vendorId / bookId: Identity of the book being inspected.
 * - title: Display title for the header.
 * - artifact: Fully hydrated cached artifact (null for jobs without a cache yet).
 * - job: In-flight codeSetDownload job (optional).
 * - backgroundJobs: All synced codeSetDownload jobs visible in this session.
 * - onRebuild / onArchive / onVerify: Cache-level action callbacks (page-owned).
 * - busyAction / verifyReport / actionError: Current cache action state.
 *
 * Rules:
 * - Must receive at least one of artifact or job.
 *
 * Sync:
 * - Purely a render contract; data flows in from the page.
 */
export interface CodeSetDetailSlideoutProps {
  vendorId: string;
  bookId: string;
  title: string;
  artifact: SystemViolationCodeSetArtifact | null;
  job: BackgroundJob | null;
  backgroundJobs: BackgroundJob[];
  onRebuild?: (mode?: 'resume' | 'restart') => void | Promise<void>;
  onArchive?: () => void | Promise<void>;
  onVerify?: () => void | Promise<void>;
  busyAction?: 'resume' | 'restart' | 'archive' | 'verify' | null;
  verifyReport?: CodeSetCacheVerifyReport | null;
  actionError?: string | null;
}

type TabKey = 'general' | 'cache' | 'departments' | 'audit';

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

function TabBar({ active, onChange }: { active: TabKey; onChange: (tab: TabKey) => void }) {
  const tabs: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'general', label: 'General', icon: BookOpen },
    { key: 'cache', label: 'Cache Status', icon: Layers3 },
    { key: 'departments', label: 'Where Used', icon: Users },
    { key: 'audit', label: 'Audit', icon: History },
  ];
  return (
    <div className="flex items-center gap-0 border-b border-gray-200 px-4 shrink-0">
      {tabs.map(({ key, label, icon: Icon }) => {
        const isActive = key === active;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              isActive
                ? 'border-orange-500 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header (always visible above the tabs)
// ---------------------------------------------------------------------------

function SlideoutHeader({
  vendorId,
  bookId,
  title,
  artifact,
  job,
}: Pick<CodeSetDetailSlideoutProps, 'vendorId' | 'bookId' | 'title' | 'artifact' | 'job'>) {
  const rawStatus = artifact?.status ?? (job?.status === 'running' ? 'downloading' : job?.status === 'pending' ? 'queued' : job?.status ?? 'unknown');
  const edition = artifact?.edition ?? artifact?.printing ?? artifact?.requestedPrinting ?? artifact?.normalized?.edition ?? String((job?.payload ?? {}).edition ?? '—');
  return (
    <div className="px-4 py-4 border-b border-gray-200 space-y-3 shrink-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
          {vendorId === 'icc' ? 'ICC Digital Codes' : vendorId === 'nfpa' ? 'NFPA LiNK' : vendorId.toUpperCase()}
        </span>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(rawStatus)}`}>
          {friendlyStatus(rawStatus)}
        </span>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-1">Book ID: {bookId} · Edition: {edition}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: General
// ---------------------------------------------------------------------------

function GeneralTab({ artifact, job }: { artifact: SystemViolationCodeSetArtifact | null; job: BackgroundJob | null }) {
  const chapterManifest = artifact?.chapterManifest ?? [];
  const legacyChapters = artifact?.normalized?.chapters ?? [];
  const chapterCount = chapterManifest.length || artifact?.chapterCount || artifact?.normalized?.chapterCount || 0;
  const sectionCount = artifact?.violationCount
    ?? chapterManifest.reduce((sum, entry) => sum + (entry.sectionCount ?? 0), 0)
    ?? artifact?.normalized?.violationCount
    ?? (typeof job?.progress?.total === 'number' ? job.progress.total : 0);

  return (
    <div className="px-4 py-4 space-y-4 text-xs">
      <section className="grid grid-cols-2 gap-3">
        <StatCard label="Chapters" value={chapterCount.toLocaleString()} />
        <StatCard label="Sections" value={(sectionCount ?? 0).toLocaleString()} />
        <StatCard label="Vendor" value={artifact?.vendorId ?? String((job?.payload ?? {}).vendorId ?? '—')} />
        <StatCard label="Book ID" value={artifact?.bookId ?? String((job?.payload ?? {}).bookId ?? '—')} mono />
        <StatCard label="Edition" value={artifact?.edition ?? artifact?.printing ?? '—'} />
        <StatCard label="Requested Printing" value={artifact?.requestedPrinting ?? '—'} />
      </section>

      {!artifact && job ? (
        <section className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800">
          <p className="font-medium mb-1">No cached artifact yet</p>
          <p className="text-[11px]">
            This row is driven by an in-flight background job. Chapter and section counts will populate once the worker
            finishes materialising the code set.
          </p>
        </section>
      ) : null}

      {chapterManifest.length > 0 ? (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Chapter Manifest</h4>
          <div className="space-y-2">
            {chapterManifest.map((entry) => (
              <div key={entry.chapterId} className="rounded-lg border border-gray-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-gray-800">Chapter {entry.ordinal || '—'} · {entry.title}</p>
                  <span className="text-[11px] text-gray-500 tabular-nums">
                    {entry.sectionCount} sections · {formatBytes(entry.bytes)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : legacyChapters.length > 0 ? (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Chapters (legacy)</h4>
          <div className="space-y-2">
            {legacyChapters.slice(0, 10).map((chapter) => (
              <div key={chapter.chapterId} className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-xs font-medium text-gray-800">
                  Chapter {chapter.chapterNumber || '—'} · {chapter.title}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 px-3 py-2">
      <p className="text-gray-400 uppercase tracking-wide text-[10px]">{label}</p>
      <p className={`text-gray-700 mt-1 ${mono ? 'font-mono break-all' : ''}`}>{value || '—'}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Cache Status
// ---------------------------------------------------------------------------

interface LiveMonitorEntry {
  timestamp: string;
  phase: string;
  percent: number;
  message?: string;
  status: string;
}

function CacheStatusTab({
  artifact,
  job,
  onRebuild,
  onArchive,
  onVerify,
  busyAction,
  verifyReport,
  actionError,
}: CodeSetDetailSlideoutProps) {
  const artifactProgress = artifact?.cacheProgress;
  const percent = typeof artifactProgress?.percent === 'number'
    ? Math.round(artifactProgress.percent)
    : typeof job?.progress?.percent === 'number'
      ? Math.round(job.progress.percent)
      : (artifact?.status === 'ready' ? 100 : 0);
  const phase = artifactProgress?.message
    ?? artifactProgress?.phase
    ?? job?.message
    ?? job?.progress?.phase
    ?? artifact?.status
    ?? 'unknown';
  const isPending = job?.status === 'pending';
  const isRunning = job?.status === 'running' || artifact?.status === 'rebuilding';
  const isFailed = job?.status === 'failed';

  // Live monitor: capture every distinct (phase, percent, message) snapshot for the active job
  // and render a scrolling timeline. We rely on the synced background_jobs liveQuery already
  // driving re-renders through the page — this effect simply appends unique transitions.
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveEntries, setLiveEntries] = useState<LiveMonitorEntry[]>([]);
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!job && !artifactProgress) return;
    const p = typeof artifactProgress?.percent === 'number'
      ? Math.round(artifactProgress.percent)
      : typeof job?.progress?.percent === 'number'
        ? Math.round(job.progress.percent)
        : 0;
    const ph = artifactProgress?.phase ?? job?.progress?.phase ?? job?.status ?? artifact?.status ?? 'unknown';
    const msg = artifactProgress?.message ?? job?.message ?? '';
    const updatedAtValue = artifactProgress?.updatedAt ?? job?.updatedAt ?? artifact?.updatedAt ?? new Date().toISOString();
    const updatedAt = updatedAtValue instanceof Date ? updatedAtValue.toISOString() : String(updatedAtValue);
    const sig = `${artifact?.status ?? job?.status}|${p}|${ph}|${msg}|${String(updatedAt)}`;
    if (sig === lastSignatureRef.current) return;
    lastSignatureRef.current = sig;
    setLiveEntries((prev) => [
      ...prev.slice(-199),
      {
        timestamp: updatedAt,
        phase: String(ph),
        percent: p,
        message: msg || undefined,
        status: job?.status ?? artifact?.status ?? 'unknown',
      },
    ]);
  }, [artifact?.status, artifact?.updatedAt, artifactProgress, job]);

  // Elapsed time ticker — re-renders once per second while the job is running.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!job && artifact?.status !== 'rebuilding') return;
    if (job && job.status !== 'running' && job.status !== 'pending' && artifact?.status !== 'rebuilding') return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [artifact?.status, job]);

  return (
    <div className="px-4 py-4 space-y-4 text-xs">
      {/* Cache actions */}
      {artifact ? (
        <section className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => void onRebuild?.('resume')}
            disabled={Boolean(busyAction)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {busyAction === 'resume' ? 'Resuming…' : 'Resume'}
          </button>
          <button
            onClick={() => void onRebuild?.('restart')}
            disabled={Boolean(busyAction)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-amber-300 text-amber-700 rounded-md hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {busyAction === 'restart' ? 'Restarting…' : 'Restart'}
          </button>
          <button
            onClick={() => void onVerify?.()}
            disabled={Boolean(busyAction)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {busyAction === 'verify' ? 'Verifying…' : 'Verify Integrity'}
          </button>
          <button
            onClick={() => void onArchive?.()}
            disabled={Boolean(busyAction)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Archive className="h-3.5 w-3.5" />
            {busyAction === 'archive' ? 'Archiving…' : 'Archive'}
          </button>
        </section>
      ) : null}

      {actionError ? (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">{actionError}</p>
      ) : null}

      {/* Live progress block (when synced artifact progress or a job is attached) */}
      {job || artifactProgress ? (
        <section className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <div className="flex items-center justify-between text-xs text-blue-700 mb-1">
            <span className="truncate font-medium">{phase}</span>
            <span className="tabular-nums">{percent}%</span>
          </div>
          <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${percent}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] text-blue-700 mt-1.5 gap-2">
            <span>Elapsed: <span className="tabular-nums">{formatElapsed(job?.startedAt, nowMs)}</span></span>
            <span>Updated: {formatDateTime(artifactProgress?.updatedAt ?? job?.updatedAt ?? artifact?.updatedAt)}</span>
          </div>
          {isPending ? (
            <p className="mt-2 text-[11px] text-amber-800">
              Queued means the job row exists but no worker has claimed it yet. If this does not move to Downloading, the
              code-set download worker is not consuming jobs.
            </p>
          ) : null}
          {isRunning ? (
            <p className="mt-2 text-[11px] text-blue-800">
              The worker is updating this cache document. Safe to leave this page; progress updates through local sync.
            </p>
          ) : null}
          {isFailed ? (
            <p className="mt-2 text-[11px] text-red-800">
              {job.error ?? job.message ?? 'Code-set download failed.'}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => setLiveOpen((v) => !v)}
            className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium border border-blue-300 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            <Radio className="h-3 w-3" />
            {liveOpen ? 'Hide live feed' : `Show live feed (${liveEntries.length})`}
          </button>

          {liveOpen ? (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-blue-200 bg-white">
              {liveEntries.length === 0 ? (
                <p className="text-[11px] text-gray-400 px-2 py-1.5">No progress updates captured yet.</p>
              ) : (
                <ol className="divide-y divide-gray-100">
                  {[...liveEntries].reverse().map((entry, idx) => (
                    <li key={`${entry.timestamp}-${idx}`} className="px-2 py-1 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-gray-700 truncate">
                          <span className="font-medium">{entry.phase}</span>
                          {entry.message ? <span className="text-gray-500"> — {entry.message}</span> : null}
                        </p>
                        <p className="text-[10px] text-gray-400">{formatDateTime(entry.timestamp)}</p>
                      </div>
                      <span className="text-[11px] text-gray-600 tabular-nums shrink-0">{entry.percent}%</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Cache metadata grid */}
      <section className="grid grid-cols-2 gap-3">
        <StatCard label="Status" value={(artifact?.status ?? (job?.status ?? 'unknown'))} />
        <StatCard label="Verify" value={artifact?.verifyStatus ?? '—'} />
        <StatCard label="Cache Size" value={formatBytes(artifact?.totalBytes)} />
        <StatCard label="Downloaded" value={formatDateTime(artifact?.downloadedAt)} />
        <StatCard label="Last Verified" value={formatDateTime(artifact?.lastVerifiedAt)} />
        <StatCard label="Cache Key" value={artifact?.cacheKey ?? '—'} mono />
      </section>

      {/* Verify report */}
      {verifyReport ? (
        <section className={`rounded-lg border px-3 py-3 text-xs ${
          verifyReport.overallStatus === 'ok'
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-red-200 bg-red-50 text-red-800'
        }`}>
          <p className="font-medium mb-1">
            Verify Integrity — {verifyReport.overallStatus === 'ok' ? 'All blobs healthy' : 'Issues found'}
          </p>
          <p>
            {verifyReport.okCount}/{verifyReport.chapterCount} chapters OK ·{' '}
            {verifyReport.mismatchCount} mismatched · {verifyReport.missingCount} missing · checked {formatDateTime(verifyReport.verifiedAt)}
          </p>
        </section>
      ) : null}

      {/* Blob paths */}
      {artifact ? (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Blob Paths</h4>
          <div className="space-y-1.5 text-[11px] font-mono text-gray-600">
            {artifact.catalogBlobPath ? (
              <div className="rounded border border-gray-200 px-2 py-1">catalog: {artifact.catalogBlobPath}</div>
            ) : null}
            {artifact.chaptersBlobPath ? (
              <div className="rounded border border-gray-200 px-2 py-1">chapters: {artifact.chaptersBlobPath}</div>
            ) : null}
            {!artifact.catalogBlobPath && !artifact.chaptersBlobPath ? (
              <p className="text-gray-400 font-sans">No blob paths — legacy artifact.</p>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Where Used (Departments)
// ---------------------------------------------------------------------------

interface DepartmentUsage {
  tenantId: string;
  tenantName: string;
  status: string;
  phase?: string;
  percent?: number;
  jobId?: string;
  error?: string;
  updatedAt?: string;
}

function WhereUsedTab({
  vendorId,
  bookId,
  backgroundJobs,
}: Pick<CodeSetDetailSlideoutProps, 'vendorId' | 'bookId' | 'backgroundJobs'>) {
  const [usages, setUsages] = useState<DepartmentUsage[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listTenants()
      .then((tenants: any[]) => {
        if (cancelled) return;
        const matches: DepartmentUsage[] = [];
        for (const tenant of tenants) {
          const vendors = tenant?.regulatoryFramework?.codeSetVendors ?? [];
          for (const vendor of vendors) {
            if ((vendor?.vendorId ?? '').toLowerCase() !== vendorId.toLowerCase()) continue;
            const book = (vendor?.selectedBooks ?? []).find((b: any) => String(b?.bookId) === String(bookId));
            if (!book) continue;
            matches.push({
              tenantId: tenant._id,
              tenantName: tenant.name ?? tenant.slug ?? tenant._id,
              status: String(book.downloadStatus ?? 'unknown'),
              phase: book.downloadPhase,
              percent: typeof book.progressPercent === 'number' ? book.progressPercent : undefined,
              jobId: book.downloadJobId,
              error: book.errorMessage,
              updatedAt: book.updatedAt ?? book.downloadedAt,
            });
          }
        }
        setUsages(matches);
      })
      .catch((error) => {
        console.warn('[WhereUsedTab] failed to read tenants:', error);
        if (!cancelled) setUsages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorId, bookId]);

  const mergedUsages = useMemo(() => {
    if (!usages) return null;
    const jobByTenant = new Map<string, BackgroundJob>();
    for (const job of backgroundJobs) {
      const jobBookId = String((job.payload ?? {}).bookId ?? '');
      if (jobBookId !== String(bookId)) continue;
      if (!job.tenantId) continue;
      const existing = jobByTenant.get(job.tenantId);
      if (!existing || new Date(job.updatedAt) > new Date(existing.updatedAt)) {
        jobByTenant.set(job.tenantId, job);
      }
    }
    return usages.map((usage) => {
      const job = jobByTenant.get(usage.tenantId);
      if (!job) return usage;
      return {
        ...usage,
        status: job.status === 'running'
          ? 'downloading'
          : job.status === 'failed'
            ? 'failed'
            : job.status === 'completed'
              ? 'completed'
              : usage.status,
        phase: job.message ?? job.progress?.phase ?? usage.phase,
        percent: typeof job.progress?.percent === 'number' ? job.progress.percent : usage.percent,
        jobId: job._id,
        error: job.error ?? usage.error,
        updatedAt: job.updatedAt ?? usage.updatedAt,
      };
    });
  }, [usages, backgroundJobs, bookId]);

  return (
    <div className="px-4 py-4 space-y-3 text-xs">
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-orange-600" />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          Departments Using This Book {mergedUsages ? `(${mergedUsages.length})` : ''}
        </h4>
      </div>
      {!mergedUsages ? (
        <p className="text-xs text-gray-400">Loading department list…</p>
      ) : mergedUsages.length === 0 ? (
        <p className="text-xs text-gray-400">No departments currently reference this book.</p>
      ) : (
        <div className="space-y-2">
          {mergedUsages.map((usage) => (
            <div key={usage.tenantId} className="rounded-lg border border-gray-200 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-gray-800 truncate">{usage.tenantName}</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadgeClass(usage.status)}`}>
                  {usage.status}
                </span>
              </div>
              {usage.phase || typeof usage.percent === 'number' ? (
                <div className="mt-1.5">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                    <span className="truncate">{usage.phase ?? ''}</span>
                    <span>{typeof usage.percent === 'number' ? `${Math.round(usage.percent)}%` : ''}</span>
                  </div>
                  {typeof usage.percent === 'number' ? (
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(0, Math.min(100, usage.percent))}%` }} />
                    </div>
                  ) : null}
                </div>
              ) : null}
              {usage.error ? (
                <p className="mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{usage.error}</p>
              ) : null}
              <div className="flex items-center justify-between text-[10px] text-gray-400 mt-1 gap-2">
                <span className="truncate">Tenant: {usage.tenantId}</span>
                {usage.jobId ? <span className="font-mono truncate">job: {usage.jobId}</span> : null}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <a
                  href={`/tenants/${encodeURIComponent(usage.tenantId)}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open department
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Audit
// ---------------------------------------------------------------------------

interface AuditEvent {
  kind: 'audit' | 'job' | 'lifecycle';
  timestamp: string;
  action: string;
  summary: string;
  status?: string;
  actor?: string;
  detail?: string;
}

function AuditTab({
  vendorId,
  bookId,
  artifact,
  backgroundJobs,
}: Pick<CodeSetDetailSlideoutProps, 'vendorId' | 'bookId' | 'artifact' | 'backgroundJobs'>) {
  const [auditRows, setAuditRows] = useState<any[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getSystemDb()
      .then(async (db: any) => {
        if (cancelled) return;
        const documentId = artifact?._id;
        // Pull audit rows for this artifact id (if we have one) plus any
        // audit rows referencing the cacheKey. This is a best-effort filter
        // since audit_logs is shared across collections.
        const rows: any[] = await db.audit_logs
          .where('collection')
          .equals('codeSetRawArtifacts')
          .toArray();
        const filtered = rows.filter((row: any) => {
          if (!documentId) return false;
          return String(row.documentId) === String(documentId);
        });
        if (!cancelled) setAuditRows(filtered);
      })
      .catch((error: unknown) => {
        console.warn('[AuditTab] failed to read audit_logs:', error);
        if (!cancelled) setAuditRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [artifact?._id]);

  const events = useMemo<AuditEvent[]>(() => {
    const list: AuditEvent[] = [];

    // Artifact lifecycle snapshots (synthetic — derived from the current doc)
    if (artifact?.downloadedAt) {
      list.push({
        kind: 'lifecycle',
        timestamp: artifact.downloadedAt,
        action: 'downloaded',
        summary: 'Cache artifact downloaded',
        status: artifact.status,
      });
    }
    if (artifact?.lastVerifiedAt) {
      list.push({
        kind: 'lifecycle',
        timestamp: artifact.lastVerifiedAt,
        action: 'verified',
        summary: 'Cache integrity verified',
        status: artifact.verifyStatus,
      });
    }

    // Real audit_logs rows for this artifact
    if (auditRows) {
      for (const row of auditRows) {
        const ts = row.createdAt
          ? (typeof row.createdAt === 'string' ? row.createdAt : new Date(row.createdAt).toISOString())
          : new Date(0).toISOString();
        list.push({
          kind: 'audit',
          timestamp: ts,
          action: String(row.action ?? 'update'),
          summary: String(row.summary ?? `${row.action ?? 'update'} codeSetRawArtifacts`),
          actor: row.actor?.email ?? row.actor?.userId ?? row.userId,
          detail: row.reason ?? undefined,
        });
      }
    }

    // Background-job history for this (vendorId, bookId)
    for (const job of backgroundJobs) {
      const payload = (job.payload ?? {}) as Record<string, unknown>;
      if (String(payload.vendorId ?? '').toLowerCase() !== vendorId.toLowerCase()) continue;
      if (String(payload.bookId ?? '') !== String(bookId)) continue;
      list.push({
        kind: 'job',
        timestamp: job.startedAt,
        action: 'job:started',
        summary: `Download job ${job._id} started`,
        status: job.status,
        actor: job.tenantId ? `tenant:${job.tenantId}` : undefined,
      });
      if (job.completedAt) {
        list.push({
          kind: 'job',
          timestamp: job.completedAt,
          action: `job:${job.status}`,
          summary: job.status === 'completed'
            ? `Download job ${job._id} completed`
            : job.status === 'failed'
              ? `Download job ${job._id} failed`
              : `Download job ${job._id} ${job.status}`,
          status: job.status,
          actor: job.tenantId ? `tenant:${job.tenantId}` : undefined,
          detail: job.error,
        });
      }
    }

    list.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return list;
  }, [artifact, auditRows, backgroundJobs, vendorId, bookId]);

  return (
    <div className="px-4 py-4 space-y-3 text-xs">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-orange-600" />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          Audit History {auditRows !== null ? `(${events.length})` : ''}
        </h4>
      </div>
      {auditRows === null ? (
        <p className="text-xs text-gray-400">Loading audit history…</p>
      ) : events.length === 0 ? (
        <p className="text-xs text-gray-400">
          No audit events yet. System cache changes, integrity checks, and download jobs will appear here.
        </p>
      ) : (
        <ol className="space-y-2">
          {events.map((event, idx) => (
            <li key={`${event.kind}-${event.timestamp}-${idx}`} className="rounded-lg border border-gray-200 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-gray-800">{event.action}</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadgeClass(event.status ?? event.kind)}`}>
                  {event.status ?? event.kind}
                </span>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">{event.summary}</p>
              <div className="flex items-center justify-between text-[10px] text-gray-400 mt-1 gap-2">
                <span>{formatDateTime(event.timestamp)}</span>
                {event.actor ? <span className="truncate">by {event.actor}</span> : null}
              </div>
              {event.detail ? (
                <p className="mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {event.detail}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @component CodeSetDetailSlideout
 *
 * Purpose:
 * - Render a four-tab detail view (General, Cache Status, Where Used, Audit) for a code set row.
 *
 * Props:
 * - See CodeSetDetailSlideoutProps.
 *
 * Behavior:
 * - Purely presentational. Reads local Dexie for "Where Used" and "Audit" via services/hooks.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No direct API calls; cache actions are callbacks passed by the page.
 *
 * Performance:
 * - Tab content remounts on tab change but page-level state is preserved in the parent.
 */
export default function CodeSetDetailSlideout(props: CodeSetDetailSlideoutProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const liveJob = resolveLiveJob(props);
  const liveProps = { ...props, job: liveJob };

  return (
    <div className="flex flex-col h-full">
      <SlideoutHeader
        vendorId={props.vendorId}
        bookId={props.bookId}
        title={props.title}
        artifact={props.artifact}
        job={liveJob}
      />
      <TabBar active={activeTab} onChange={setActiveTab} />
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'general' ? <GeneralTab artifact={props.artifact} job={liveJob} /> : null}
        {activeTab === 'cache' ? <CacheStatusTab {...liveProps} /> : null}
        {activeTab === 'departments' ? (
          <WhereUsedTab vendorId={props.vendorId} bookId={props.bookId} backgroundJobs={props.backgroundJobs} />
        ) : null}
        {activeTab === 'audit' ? (
          <AuditTab
            vendorId={props.vendorId}
            bookId={props.bookId}
            artifact={props.artifact}
            backgroundJobs={props.backgroundJobs}
          />
        ) : null}
      </div>
    </div>
  );
}
